import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePermission } from '@backstage/plugin-permission-react';
import type { TrackDefinition } from '@internal/backstage-plugin-pulse-common';
import {
  pulseTrackUpdatePermission,
  pulseTrackDeletePermission,
  pulseTrackRecertifyPermission,
  toPulseTrackDefinitionResourceRef,
} from '@internal/backstage-plugin-pulse-common';
import { useRouteRef } from '@backstage/frontend-plugin-api';
import { tracksEditSubRouteRef } from '../../routes';
import { useDeleteTrack, useRecertifyTrack } from '../../hooks';
import { downloadTrackYaml } from './export-track';
import { getTrackActionReasons } from './track-action-reasons';
import { TrackActionsMenu } from './track-actions-menu';
import { TrackDeleteDialog } from './track-delete-dialog';
import { TrackRecertifyDialog } from './track-recertify-dialog';

type TrackActionsButtonProps = {
  track: TrackDefinition;
  onDeleted?: () => void;
};

export const TrackActionsButton = ({
  track,
  onDeleted,
}: TrackActionsButtonProps) => {
  const navigate = useNavigate();
  const getEditPath = useRouteRef(tracksEditSubRouteRef);
  const { allowed: canEdit } = usePermission({
    permission: pulseTrackUpdatePermission,
    resourceRef: toPulseTrackDefinitionResourceRef(track.id),
  });
  const { allowed: canDelete } = usePermission({
    permission: pulseTrackDeletePermission,
    resourceRef: toPulseTrackDefinitionResourceRef(track.id),
  });
  const { allowed: canRecertify } = usePermission({
    permission: pulseTrackRecertifyPermission,
    resourceRef: toPulseTrackDefinitionResourceRef(track.id),
  });
  const { remove, loading: deleting } = useDeleteTrack();
  const { recertify, loading: recertifying } = useRecertifyTrack();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [recertifyOpen, setRecertifyOpen] = useState(false);

  const reasons = getTrackActionReasons(track, {
    canEdit,
    canDelete,
    canRecertify,
  });

  return (
    <>
      <TrackActionsMenu
        track={track}
        reasons={reasons}
        onExport={() => downloadTrackYaml(track)}
        onEdit={() => navigate(getEditPath?.({ id: track.id }) ?? '')}
        onDeleteRequest={() => setDeleteOpen(true)}
        onRecertifyRequest={() => setRecertifyOpen(true)}
        onOpenDocumentation={() => {
          if (track.documentationUrl) {
            window.open(
              track.documentationUrl,
              '_blank',
              'noopener,noreferrer',
            );
          }
        }}
      />
      <TrackDeleteDialog
        track={track}
        isOpen={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        remove={remove}
        deleting={deleting}
        onDeleted={onDeleted}
      />
      <TrackRecertifyDialog
        track={track}
        isOpen={recertifyOpen}
        onClose={() => setRecertifyOpen(false)}
        recertify={recertify}
        recertifying={recertifying}
      />
    </>
  );
};
